import { BreadcrumbProps, Spinner } from '@blueprintjs/core'
import { Callout } from '../../components/ui/callout'
import { Card } from '../../components/ui/card'
import React, { useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { Layout } from '../../components/Layout'
import { CardHeader } from '../../components/ui/CardHeader'
import { axiosGet } from '../../util/api'
import { Button } from '../../components/ui/button'

export const ExportAsinsPage = () => {
    const [groupedAsinsByAccountId, setGroupedAsinsByAccountId] = useState<{
        [key: number]: number[]
    }>({})
    const [initialLoading, setInitialLoading] = useState<boolean>(true)
    const [loading, setLoading] = useState<string | undefined>(undefined)
    const [noData, setNoData] = useState<boolean>(false)

    const load = () => {
        setInitialLoading(true)
        setGroupedAsinsByAccountId({})
        axiosGet('asins').then((res) => {
            setInitialLoading(false)
            if (res.data.length == 0) {
                setNoData(true)
            }

            res.data.map((item: BrowseResponse) => {
                setGroupedAsinsByAccountId((prev) => {
                    if (!prev) {
                        return {
                            [item.account_id]: [item.marketplace_id],
                        }
                    }

                    if (prev.hasOwnProperty(item.account_id)) {
                        return {
                            ...prev,
                            ...{
                                [item.account_id]: [...prev[item.account_id], item.marketplace_id],
                            },
                        }
                    } else {
                        return {
                            ...prev,
                            [item.account_id]: [item.marketplace_id],
                        }
                    }
                })
            })
        })
    }

    const download = (account_id: number, marketplace_id: number) => {
        setLoading(marketplace_id.toString() + account_id.toString())
        axiosGet('asins/export', {
            account_id,
            marketplace_id,
        }).then((r) => {
            setLoading(undefined)
            const [, filename] = r.headers['content-disposition'].split('filename=')
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([r.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename + '.csv')

            // Append to html link element page
            document.body.appendChild(link)

            // Start download
            link.click()

            // Clean up and remove the link
            link.parentNode?.removeChild(link)
        })
    }

    useEffect(() => {
        load()
    }, [])

    const flagIcon = useCallback(
        (code) => {
            let flagCode = ''
            switch (code) {
                case 1:
                    flagCode = 'us'
                    break
                case 2:
                    flagCode = 'gb'
                    break
                case 3:
                    flagCode = 'de'
                    break
                case 4:
                    flagCode = 'fr'
                    break
                case 5:
                    flagCode = 'jp'
                    break
                case 6:
                    flagCode = 'ca'
                    break
                case 8:
                    flagCode = 'it'
                    break
                case 9:
                    flagCode = 'es'
                    break
                case 10:
                    flagCode = 'in'
                    break
                case 11:
                    flagCode = 'mx'
                    break
                case 51:
                    flagCode = 'br'
                    break
                case 52:
                    flagCode = 'ae'
                    break
                case 53:
                    flagCode = 'eg'
                    break
                case 54:
                    flagCode = 'ne'
                    break
                case 55:
                    flagCode = 'sa'
                    break
                case 56:
                    flagCode = 'se'
                    break
                case 57:
                    flagCode = 'tr'
                    break
                case 58:
                    flagCode = 'sg'
                    break
                case 59:
                    flagCode = 'au'
                    break
            }
            return (
                <ReactCountryFlag
                    /* @ts-ignore*/
                    title={flagCode}
                    countryCode={flagCode}
                    svg
                    className="text-lg"
                />
            )
        },
        [groupedAsinsByAccountId],
    )

    const exportsGroupedByAccountAndMarketplace = useMemo(() => {
        const result = []
        let children = []
        const childrenCard = (children: ReactNode, key: string) => {
            return (
                <Card key={key} elevation={2} className="m-2 p-[20px]">
                    {children}
                </Card>
            )
        }

        let i = 0
        for (const accountId in groupedAsinsByAccountId) {
            if (groupedAsinsByAccountId.hasOwnProperty(accountId)) {
                children = []
                children.push(
                    <div
                        key={'i-' + i}
                        className="text-sm text-bold ml-2 mb-2"
                        style={{ flexBasis: '100%' }}
                    >
                        Account #{accountId}
                    </div>,
                )
                groupedAsinsByAccountId[accountId].forEach((marketplaceId) => {
                    if (loading == marketplaceId + accountId) {
                        children.push(
                            <Button
                                key={i}
                                className="m-2"
                                onClick={() => download(parseInt(accountId), marketplaceId)}
                            >
                                <Spinner size={20} />
                            </Button>,
                        )
                    } else {
                        children.push(
                            <Button
                                key={i}
                                className="m-2"
                                onClick={() => download(parseInt(accountId), marketplaceId)}
                            >
                                {flagIcon(marketplaceId)}
                            </Button>,
                        )
                    }
                    i++
                })

                result.push(childrenCard(children, i.toString()))
            }
        }

        return result
    }, [groupedAsinsByAccountId, loading])

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Tools' }, { href: '', text: 'Export ASINs' }]

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <div className="container">
                <div className="w-2/3 mx-auto">
                    <Callout intent="primary" className="mt-5">
                        If you have setup the&nbsp;
                        <a
                            target="_blank"
                            href="https://chrome.google.com/webstore/detail/merch-jar/gegbbecbgcjdhmkdiecdcihioeaggegk"
                            rel="noreferrer"
                        >
                            Merch Jar Chrome Extension
                        </a>
                        , then you can export a list of all your ASINs for your account here. This
                        can be used to create advertising campaigns (especially Lottery campaigns!)
                    </Callout>
                </div>
                <Card className="mt-10 w-2/3 mx-auto p-[20px]">
                    {!noData ? (
                        <div>
                            <CardHeader>
                                <h3>Export ASINs</h3>
                            </CardHeader>
                            <div className="flex flex-wrap ml-3 mt-5">
                                {initialLoading ? (
                                    <Spinner size={20} />
                                ) : (
                                    exportsGroupedByAccountAndMarketplace
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <CardHeader>
                                <h3>No Exported ASINs found</h3>
                            </CardHeader>
                            <div className="flex flex-wrap ml-3 mt-5">
                                If you'd like to export asins, please install the&nbsp;
                                <a
                                    target="_blank"
                                    href="https://chrome.google.com/webstore/detail/merch-jar/gegbbecbgcjdhmkdiecdcihioeaggegk"
                                    rel="noreferrer"
                                >
                                    Merch Jar Chrome Extension
                                </a>
                                .
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </Layout>
    )
}

interface BrowseResponse {
    account_id: number
    marketplace_id: number
}
